import { EpiPage, LoadingStatus } from '@api/types';
import { CartProvider } from '@context/CartContext';
import { Box } from '@mui/material';
import { lazy, memo, Suspense } from 'react';

const LazyMeta = lazy(() =>
  import('@components/cms/Meta/Meta').then((module) => ({ default: module.Meta }))
);

const LazyHeaderContentArea = lazy(() =>
  import('@components/layouts/areas/HeaderContentArea').then((module) => ({
    default: module.HeaderContentArea,
  }))
);

const LazyAfterHeaderContentArea = lazy(() =>
  import('@components/layouts/areas/AfterHeaderContentArea').then((module) => ({
    default: module.AfterHeaderContentArea,
  }))
);

const LazyMainContentArea = lazy(() =>
  import('@components/layouts/areas/MainContentArea').then((module) => ({
    default: module.MainContentArea,
  }))
);

const LazyFooterContentArea = lazy(() =>
  import('@components/layouts/areas/FooterContentArea').then((module) => ({
    default: module.FooterContentArea,
  }))
);
const LazyBreadCrumbsBlock = lazy(() =>
  import('@components/blocks/navigation/BreadCrumbsBlock/BreadCrumbsBlock').then((module) => ({
    default: module.BreadCrumbsBlock,
  }))
);

export type ContentPageLayoutProps = {
  page?: EpiPage;
  status?: LoadingStatus;
  children?: React.ReactNode;
};

/**
 * Default layout with Header, AfterHeader, Main and Footer areas.
 */
export const ContentPageLayout = memo(
  ({ page = {}, status = {}, children }: ContentPageLayoutProps) => {
    const isHomePage = page?.contentType?.some((x) => x === 'HomePage');
    return (
      <CartProvider>
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Suspense fallback={null}>
            <LazyMeta page={page} />
          </Suspense>

          <Suspense fallback={null}>
            <LazyHeaderContentArea />
          </Suspense>
          <Suspense fallback={null}>
            <LazyAfterHeaderContentArea />
          </Suspense>

          {!isHomePage ? (
            <Suspense fallback={null}>
              <LazyBreadCrumbsBlock />
            </Suspense>
          ) : null}
          <Suspense fallback={null}>
            <LazyMainContentArea page={page} status={status}>
              {children}
            </LazyMainContentArea>
          </Suspense>

          <Box sx={{ mt: 'auto' }} className='footer-section'>
            <Suspense fallback={null}>
              <LazyFooterContentArea />
            </Suspense>
          </Box>
        </Box>
      </CartProvider>
    );
  }
);
